import type { AdGroupSummary, CampaignAdGroupsResponse } from "@adpilot/shared";
import {
  IconArrowDown,
  IconArrowsUpDown,
  IconArrowUp,
  IconDownload,
  IconMaximize,
  IconPlus,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnCustomizerDialog } from "@/components/campaign/ColumnCustomizerDialog";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { UnavailableControl } from "@/components/patterns/unavailable-control";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  AD_GROUP_COLUMNS,
  aggregateGroups,
  downloadCsv,
  exportGroupsCsv,
  formatGroupMetric,
  type GroupColumn,
  type GroupSortKey,
  groupMetric,
  isSkuColumn,
  MONETARY_COLUMNS,
  sortGroups,
} from "./ad-groups-table-model";

export interface CampaignAdGroupsTableProps {
  campaignId: string;
  children?: ReactNode;
  data?: CampaignAdGroupsResponse;
  previousData?: CampaignAdGroupsResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  comparison: boolean;
  onComparisonChange: (value: boolean) => void;
  comparisonLoading?: boolean;
  comparisonError?: boolean;
  onComparisonRetry?: () => void;
  renderAdGroupLink?: (group: AdGroupSummary) => ReactNode;
}

const FIXED_COLUMNS = ["active", "name"] as const;
type FixedColumn = (typeof FIXED_COLUMNS)[number];
const ALL_COLUMNS: readonly (FixedColumn | GroupColumn)[] = [...FIXED_COLUMNS, ...AD_GROUP_COLUMNS];
const STATUS_FILTERS = ["all", "enabled", "paused", "archived"] as const;
const PAGE_SIZES = [25, 50, 100] as const;
/** Fields the current report contract cannot supply; rendered as "—" and not sortable. */
const UNAVAILABLE_COLUMNS: readonly GroupColumn[] = ["status", "topOfSearch"];
// Mirrors the campaigns list: switch column pinned at 0, name column pinned after it.
const SWITCH_STICKY = "sticky left-0 z-20 w-16 min-w-16 max-w-16 border-r border-line";
const NAME_STICKY =
  "sticky left-16 z-20 min-w-56 border-r border-line shadow-[4px_0_8px_-7px_rgb(15_23_42_/_0.45)]";

export function CampaignAdGroupsTable(props: CampaignAdGroupsTableProps) {
  const { t, i18n } = useTranslation("campaign");
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.adGroupsList.${key}`, options);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>("all");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(PAGE_SIZES[0]);
  const [sort, setSort] = useState<{ key: GroupSortKey; direction: "asc" | "desc" }>({
    key: "name",
    direction: "asc",
  });
  const [columns, setColumns] = useState<readonly GroupColumn[]>(AD_GROUP_COLUMNS);
  const [expanded, setExpanded] = useState(false);
  const rows = props.data?.connected ? props.data.adGroups : [];
  const filtered = sortGroups(
    rows.filter(
      (row) =>
        row.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) &&
        (status === "all" || row.status === status),
    ),
    sort.key,
    sort.direction,
  );
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * size, currentPage * size);
  const total = aggregateGroups(filtered);
  const currency = props.data?.currency ?? null;
  const previous = new Map(props.previousData?.adGroups.map((r) => [r.id, r]));
  const previousTotal = aggregateGroups(
    filtered.flatMap((r) => (previous.get(r.id) ? [previous.get(r.id) as AdGroupSummary] : [])),
  );
  const reset = () => setPage(1);
  const label = (key: GroupSortKey | FixedColumn) =>
    key === "active" ? text("active") : text(`columns.${key}`);
  const format = (row: AdGroupSummary, key: GroupColumn) =>
    formatGroupMetric(groupMetric(row, key), key, currency, i18n.language);
  const metric = (row: AdGroupSummary, key: GroupColumn, old?: AdGroupSummary) => {
    const value = groupMetric(row, key);
    // Never compare money across currencies; previous-period money needs the same currency.
    const currencyMatches = props.previousData?.currency === currency && currency !== null;
    const usableOld = old && (!MONETARY_COLUMNS.includes(key) || currencyMatches) ? old : undefined;
    const before = usableOld ? groupMetric(usableOld, key) : null;
    const delta =
      value !== null && before !== null && before !== 0
        ? (value - before) / Math.abs(before)
        : null;
    return (
      <>
        <span className="font-mono tabular-nums text-ink">{format(row, key)}</span>
        {props.comparison && key !== "defaultBid" && !UNAVAILABLE_COLUMNS.includes(key) && (
          <div
            className="font-mono text-[10px] leading-3.5 tabular-nums text-muted-foreground"
            title={text("previous")}
          >
            {props.comparisonLoading ? (
              text("loading")
            ) : props.comparisonError ? (
              "—"
            ) : (
              <>
                {usableOld ? format(usableOld, key) : "—"} ·{" "}
                {delta === null
                  ? "—"
                  : new Intl.NumberFormat(i18n.language, {
                      style: "percent",
                      maximumFractionDigits: 2,
                      signDisplay: "exceptZero",
                    }).format(delta)}
              </>
            )}
          </div>
        )}
      </>
    );
  };
  const sortBy = (key: GroupSortKey) => {
    setSort({ key, direction: sort.key === key && sort.direction === "asc" ? "desc" : "asc" });
    setPage(1);
  };
  const cellTitle = (row: AdGroupSummary, key: GroupColumn) =>
    key === "defaultBid"
      ? text("writeUnavailable")
      : UNAVAILABLE_COLUMNS.includes(key)
        ? text("reportUnavailable")
        : isSkuColumn(key) && row.hasMetrics && !row.skuBreakdown
          ? text("skuBreakdownPending")
          : undefined;
  const exportCsv = () => {
    downloadCsv(
      `campaign-${props.campaignId}-ad-groups.csv`,
      exportGroupsCsv(filtered, ["name", ...columns], label, currency, i18n.language),
    );
  };

  const sortHeader = (key: GroupSortKey, className?: string) => {
    const active = sort.key === key;
    const sortable = !UNAVAILABLE_COLUMNS.includes(key as GroupColumn);
    return (
      <TableHead
        key={key}
        className={className}
        aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
        title={sortable ? undefined : text("reportUnavailable")}
      >
        {sortable ? (
          <button
            type="button"
            aria-label={label(key)}
            className="flex w-full min-w-0 items-center gap-1 truncate text-left uppercase outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => sortBy(key)}
          >
            <span className="truncate">{label(key)}</span>
            {active && sort.direction === "asc" ? (
              <IconArrowUp aria-hidden="true" className="size-3 shrink-0" />
            ) : active ? (
              <IconArrowDown aria-hidden="true" className="size-3 shrink-0" />
            ) : (
              <IconArrowsUpDown aria-hidden="true" className="size-3 shrink-0 opacity-45" />
            )}
          </button>
        ) : (
          <span className="truncate">{label(key)}</span>
        )}
      </TableHead>
    );
  };

  const table = (
    <DataTable density="compact" stickyHeader aria-label={text("title")}>
      <TableHeader>
        <TableRow>
          <TableHead className={cn(SWITCH_STICKY, "z-30")}>{text("active")}</TableHead>
          {sortHeader("name", cn(NAME_STICKY, "z-30"))}
          {columns.map((key) => sortHeader(key, "text-right [&>button]:justify-end"))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {visible.map((row) => (
          <TableRow key={row.id} className="hover:bg-table-row-hover">
            <TableCell className={cn(SWITCH_STICKY, "bg-surface")} title={text("writeUnavailable")}>
              <Switch
                aria-label={`${text("active")} ${row.name}`}
                checked={row.status === "enabled"}
                disabled
              />
            </TableCell>
            <TableCell className={cn(NAME_STICKY, "bg-surface")}>
              <span
                className="block max-w-64 truncate text-[11px] font-semibold leading-4 text-ink"
                title={row.name}
              >
                {props.renderAdGroupLink ? (
                  props.renderAdGroupLink(row)
                ) : (
                  <Link
                    to="/campaigns/$id/ad-groups/$adGroupId"
                    params={{ id: props.campaignId, adGroupId: row.id }}
                  >
                    {row.name}
                  </Link>
                )}
              </span>
            </TableCell>
            {columns.map((key) => (
              <TableCell key={key} className="text-right" title={cellTitle(row, key)}>
                {key === "status" ? "—" : metric(row, key, previous.get(row.id))}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {/* A single row is its own total; only summarise when there is more than one. */}
      {filtered.length > 1 && (
        <TableFooter className="bg-subtle text-[11px] font-medium">
          <TableRow className="hover:bg-subtle">
            <TableCell colSpan={2} className="sticky left-0 z-20 border-r border-line bg-subtle">
              <span
                title={
                  !total.hasMetrics && filtered.length > 0 ? text("incompleteTotal") : undefined
                }
              >
                {text("total")}
                {!total.hasMetrics && filtered.length > 0 && (
                  <span className="block max-w-72 whitespace-normal text-[10px] font-normal leading-3.5 text-muted-foreground">
                    {text("incompleteTotal")}
                  </span>
                )}
              </span>
            </TableCell>
            {columns.map((key) => (
              <TableCell
                key={key}
                className="text-right"
                title={
                  isSkuColumn(key) && total.hasMetrics && !total.skuBreakdown
                    ? text("skuBreakdownPending")
                    : undefined
                }
              >
                {key === "status" ? "—" : metric(total, key, previousTotal)}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      )}
    </DataTable>
  );

  const disabledControl = (labelText: string, reason: string) => (
    <UnavailableControl variant="outline" reason={reason}>
      {labelText}
    </UnavailableControl>
  );

  const toolbar = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <UnavailableControl reason={text("writeUnavailable")}>
          <IconPlus data-icon="inline-start" />
          {text("create")}
        </UnavailableControl>
        {disabledControl("ASIN", text("productUnavailable"))}
        <FilterSelect
          ariaLabel={text("activeFilter")}
          items={STATUS_FILTERS.map((value) => ({
            value,
            label: text(`states.${value}`),
          }))}
          value={status}
          onValueChange={(value) => {
            setStatus(value as (typeof STATUS_FILTERS)[number]);
            reset();
          }}
        />
        {disabledControl(text("statusFilter"), text("reportUnavailable"))}
        {disabledControl(text("conditions"), text("conditionsUnavailable"))}
      </div>
      <div className="flex items-center gap-2">
        <SearchInput
          aria-label={text("search")}
          placeholder={text("search")}
          value={search}
          showIcon
          textSize="compact"
          className="w-[200px]"
          onChange={(event) => {
            setSearch(event.target.value);
            reset();
          }}
        />
        <label
          htmlFor={`ad-groups-comparison-${props.campaignId}`}
          className="flex items-center gap-1.5 whitespace-nowrap text-[11px] text-muted-foreground"
        >
          {text("comparison")}
          <Switch
            id={`ad-groups-comparison-${props.campaignId}`}
            aria-label={text("comparison")}
            checked={props.comparison}
            onCheckedChange={props.onComparisonChange}
          />
        </label>
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={text("export")}
          title={text("export")}
          disabled={filtered.length === 0 || props.isLoading || props.isError}
          onClick={exportCsv}
        >
          <IconDownload />
        </ControlButton>
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={text("fullscreen")}
          title={text("fullscreen")}
          onClick={() => setExpanded(true)}
        >
          <IconMaximize />
        </ControlButton>
        <ColumnCustomizerDialog
          allColumns={ALL_COLUMNS}
          fixedColumns={FIXED_COLUMNS}
          defaultColumns={AD_GROUP_COLUMNS}
          visibleColumns={columns}
          label={(column) => label(column)}
          description={text("customColumnsDescription")}
          onApply={(next) =>
            setColumns(next.filter((c): c is GroupColumn => c !== "active" && c !== "name"))
          }
        />
      </div>
    </>
  );

  const body = props.isLoading ? (
    <div className="flex flex-col gap-2 p-3.5" role="status" aria-label={text("loading")}>
      {[1, 2, 3, 4].map((n) => (
        <Skeleton key={n} className="h-8 w-full" />
      ))}
    </div>
  ) : props.isError ? (
    <div className="p-3.5">
      <ErrorState
        message={text("loadFailed")}
        onRetry={props.onRetry}
        isRetrying={props.isFetching}
      />
    </div>
  ) : filtered.length === 0 ? (
    <div className="p-3.5">
      <EmptyState
        title={text("empty")}
        description={text(rows.length ? "noMatches" : "emptyDescription")}
      />
    </div>
  ) : (
    table
  );

  return (
    <section
      aria-label={text("title")}
      className="flex flex-col gap-[14px]"
      aria-busy={props.isFetching}
    >
      {props.children}
      <DataTableFrame
        toolbar={toolbar}
        footer={
          <DataTablePagination
            page={currentPage}
            pageCount={pages}
            pageSize={size}
            pageSizeOptions={PAGE_SIZES}
            summary={text("count", { count: filtered.length })}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setSize(next);
              reset();
            }}
          />
        }
      >
        {props.comparison && props.comparisonError && (
          <div className="border-b border-line p-3.5">
            <ErrorState
              variant="inline"
              message={text("comparisonFailed")}
              onRetry={props.onComparisonRetry}
              isRetrying={props.comparisonLoading}
            />
          </div>
        )}
        {body}
      </DataTableFrame>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogPopup className="w-[95vw] max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>{text("title")}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[80vh] overflow-auto px-6 pb-6">{table}</div>
        </DialogPopup>
      </Dialog>
    </section>
  );
}
